export function formatMoney(value: number | null | undefined): string {
  const num = Number(value ?? 0);
  if (!Number.isFinite(num)) return '0 ₽';
  const hasKopecks = Math.abs(num - Math.round(num)) >= 0.005;
  return `${num.toLocaleString('ru-RU', {
    minimumFractionDigits: hasKopecks ? 2 : 0,
    maximumFractionDigits: hasKopecks ? 2 : 0,
  })} ₽`;
}

export function formatMillions(value: number | null | undefined): string {
  const num = Number(value ?? 0);
  if (!Number.isFinite(num)) return '0,00 млн ₽';
  if (Math.abs(num) >= 1_000_000) {
    return `${(num / 1_000_000).toLocaleString('ru-RU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} млн ₽`;
  }
  return formatMoney(num);
}

export function formatDateRu(isoDate: string | null | undefined): string {
  if (!isoDate) return '—';
  const d = isoDate.slice(0, 10);
  const parts = d.split('-');
  if (parts.length !== 3) return isoDate;
  return `${parts[2]}.${parts[1]}.${parts[0]}`;
}

export function formatDateTimeRu(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return iso;
    return dt.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export type BadgeTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

export interface GateBadgeInfo {
  code: string;
  label: string;
  tone: BadgeTone;
  tooltip: string;
}

export function getContractGateInfo(status: string | null | undefined): GateBadgeInfo {
  switch (status) {
    case 'passed':
      return {
        code: 'Д',
        label: 'Договор ок',
        tone: 'success',
        tooltip: 'Гейт 1 (Договор): договор подписан, активен, юр.рисков не выявлено',
      };
    case 'warning':
      return {
        code: 'Д',
        label: 'Договор ?',
        tone: 'warning',
        tooltip: 'Гейт 1 (Договор): договор на согласовании либо не привязан к заявке',
      };
    case 'blocked':
      return {
        code: 'Д',
        label: 'Договор стоп',
        tone: 'danger',
        tooltip: 'Гейт 1 (Договор): договор заблокирован, расторгнут либо в суде',
      };
    default:
      return {
        code: 'Д',
        label: 'Договор —',
        tone: 'neutral',
        tooltip: 'Гейт 1 (Договор): статус не определён',
      };
  }
}

export function getPrimaryDocGateInfo(status: string | null | undefined): GateBadgeInfo {
  switch (status) {
    case 'passed':
      return {
        code: 'П',
        label: 'Первичка ок',
        tone: 'success',
        tooltip: 'Гейт 2 (Первичка): счёт, УПД или КС-2 проверены и подтверждены',
      };
    case 'warning':
      return {
        code: 'П',
        label: 'Первичка ?',
        tone: 'warning',
        tooltip: 'Гейт 2 (Первичка): отсутствует ссылка на первичный документ',
      };
    case 'blocked':
      return {
        code: 'П',
        label: 'Первичка стоп',
        tone: 'danger',
        tooltip: 'Гейт 2 (Первичка): первичный документ отклонён или содержит рекламацию',
      };
    default:
      return {
        code: 'П',
        label: 'Первичка —',
        tone: 'neutral',
        tooltip: 'Гейт 2 (Первичка): статус не определён',
      };
  }
}

export function getBudgetGateInfo(status: string | null | undefined): GateBadgeInfo {
  switch (status) {
    case 'within_limit':
      return {
        code: 'Б',
        label: 'БДДС в лимите',
        tone: 'success',
        tooltip: 'Гейт 3 (БДДС): платёж в пределах утверждённого лимита статьи',
      };
    case 'within_tolerance':
      return {
        code: 'Б',
        label: 'БДДС допуск',
        tone: 'warning',
        tooltip: 'Гейт 3 (БДДС): превышение лимита в пределах 5% допуска',
      };
    case 'over_limit':
      return {
        code: 'Б',
        label: 'БДДС сверх',
        tone: 'danger',
        tooltip: 'Гейт 3 (БДДС): превышение утверждённого лимита статьи бюджета',
      };
    case 'unbudgeted':
      return {
        code: 'Б',
        label: 'Вне бюджета',
        tone: 'danger',
        tooltip: 'Гейт 3 (БДДС): внеплановая статья расходов',
      };
    default:
      return {
        code: 'Б',
        label: 'БДДС —',
        tone: 'neutral',
        tooltip: 'Гейт 3 (БДДС): статус не определён',
      };
  }
}

export function getAccountRoutingGateInfo(status: string | null | undefined): GateBadgeInfo {
  switch (status) {
    case 'settlement_ok':
      return {
        code: 'С',
        label: 'Р/с ок',
        tone: 'success',
        tooltip: 'Гейт 4 (Счёт): списание с расчётного счёта, средств достаточно',
      };
    case 'obs_targeted':
      return {
        code: 'С',
        label: 'ОБС спецсчёт',
        tone: 'info',
        tooltip: 'Гейт 4 (Счёт): целевое расходование со спецсчёта ОБС (сопровождение ГОЗ/проекта)',
      };
    case 'tax_reserve_ok':
      return {
        code: 'С',
        label: 'ЕНС резерв',
        tone: 'success',
        tooltip: 'Гейт 4 (Счёт): списание со специального налогового резерва ЕНС',
      };
    case 'insufficient_funds':
      return {
        code: 'С',
        label: 'Нет средств',
        tone: 'danger',
        tooltip: 'Гейт 4 (Счёт): остатка на счёте недостаточно для проведения платежа',
      };
    default:
      return {
        code: 'С',
        label: 'Счёт —',
        tone: 'neutral',
        tooltip: 'Гейт 4 (Счёт): счёт не определён',
      };
  }
}

export const SOURCE_MODULE_CONFIG: Record<string, { label: string; tone: BadgeTone }> = {
  mto: { label: 'МТО / Снабжение', tone: 'info' },
  logist: { label: 'Логистика', tone: 'info' },
  smeta: { label: 'Сметчик (КС-3)', tone: 'info' },
  chronum_payroll: { label: 'Хронум (ФОТ)', tone: 'warning' },
  taxes_ens: { label: 'Налоги ЕНС', tone: 'danger' },
  debt_leasing: { label: 'Кредиты/Лизинг', tone: 'neutral' },
  statut: { label: 'Юристы', tone: 'neutral' },
  manual: { label: 'Ручной ввод', tone: 'neutral' },
  // «Заявки на оплату»: счёт приложил сотрудник любой службы.
  staff: { label: 'Заявка сотрудника', tone: 'info' },
};

/** Инициатор заявки сотрудника без отдела в скобках: отдел показываем рядом. */
export function staffInitiatorName(req: {
  initiator_name: string | null;
  staff_department: string | null;
}): string {
  const name = req.initiator_name ?? '';
  const suffix = req.staff_department ? ` (${req.staff_department})` : '';
  return suffix && name.endsWith(suffix) ? name.slice(0, -suffix.length) : name;
}

export const REQUEST_STATUS_CONFIG: Record<string, { label: string; tone: BadgeTone }> = {
  draft: { label: 'Черновик', tone: 'neutral' },
  in_review: { label: 'На финконтроле', tone: 'warning' },
  ready_for_ceo: { label: 'Готово к Гендиректору', tone: 'info' },
  on_ceo_approval: { label: 'На акцепте у Гендиректора', tone: 'warning' },
  approved: { label: 'Акцептовано Гендиректором', tone: 'success' },
  deferred: { label: 'Отложено', tone: 'warning' },
  rejected: { label: 'Отклонено', tone: 'danger' },
  exported_1c: { label: 'Выгружено в 1С', tone: 'success' },
  executed: { label: 'Исполнено банком', tone: 'success' },
};

export const PRIORITY_CONFIG: Record<string, { label: string; tone: BadgeTone }> = {
  critical: { label: 'Критичный', tone: 'danger' },
  high: { label: 'Высокий', tone: 'warning' },
  normal: { label: 'Плановый', tone: 'neutral' },
  low: { label: 'Низкий', tone: 'neutral' },
};

export const ADVANCE_RETENTION_DIRECTION_LABELS: Record<string, string> = {
  payable_advance: 'Аванс выданный (подрядчикам/поставщикам)',
  receivable_advance: 'Аванс полученный (от Заказчика)',
  receivable_retention: 'Гарантийное удержание к возврату (от Заказчика)',
  payable_retention: 'Гарантийное удержание к выплате (субподрядчику)',
};

export const TAX_TYPE_LABELS: Record<string, string> = {
  vat_1_3: '1/3 НДС квартала',
  ndfl: 'НДФЛ с выплат работникам',
  insurance_contrib: 'Страховые взносы',
  profit_tax: 'Налог на прибыль',
  property_tax: 'Налог на имущество',
  ens_replenishment: 'Пополнение ЕНС',
};

export const REGISTRY_STATUS_CONFIG: Record<string, { label: string; tone: BadgeTone }> = {
  forming: { label: 'Формирование', tone: 'neutral' },
  on_approval: { label: 'На акцепте', tone: 'warning' },
  approved: { label: 'Утверждён', tone: 'success' },
  executing: { label: 'Исполняется', tone: 'info' },
  closed: { label: 'Закрыт', tone: 'neutral' },
};

export const PAYMENT_EXECUTION_STATUS_CONFIG: Record<string, { label: string; tone: BadgeTone }> = {
  paid: { label: 'Оплачено', tone: 'success' },
  partially_paid: { label: 'Частично', tone: 'warning' },
  unpaid: { label: 'Не оплачено', tone: 'neutral' },
};

export const COUNTERPARTY_TYPE_LABELS: Record<string, string> = {
  supplier: 'Поставщик',
  subcontractor: 'Субподрядчик',
  customer: 'Заказчик',
  leasing: 'Лизинг',
  bank: 'Банк',
  tax_authority: 'ФНС / Налоги',
  other: 'Прочее',
};

export const COUNTERPARTY_SOURCE_LABELS: Record<string, { label: string; tone: BadgeTone }> = {
  '1c_api': { label: '1С:API', tone: 'info' },
  '1c_statement': { label: '1С:Выписка', tone: 'info' },
  '1c_document': { label: '1С:Документ', tone: 'info' },
  excel: { label: 'Excel', tone: 'warning' },
  contracts: { label: 'Договоры', tone: 'neutral' },
  manual: { label: 'Ручной ввод', tone: 'neutral' },
};
